/**
 * Konstanten für die Portfolio Wizard Anwendung
 * Zentrale Konfigurationswerte und statische Daten
 */

import { KirchensteuerKonfiguration, SparerfreibetragKonfiguration } from '@/types';

// Verfügbare Steuerjahre
export const STEUERJAHRE = [2026, 2027];

// Standardwerte
export const STANDARD_STEUERJAHR = 2026;
export const STANDARD_FAMILIENSTAND = 'verheiratet';
export const STANDARD_KIRCHENSTEUER = 'keine';

// Familienstand-Optionen
export const FAMILIENSTAND_OPTIONEN = [
  { value: 'alleinstehend', label: 'Alleinstehend' },
  { value: 'verheiratet', label: 'Verheiratet/Verpartnert' }
] as const;

// Kirchensteuer-Konfiguration
export const KIRCHENSTEUER_OPTIONEN: KirchensteuerKonfiguration[] = [
  {
    key: 'keine',
    label: 'Keine Kirchensteuer',
    prozentsatz: 0
  },
  {
    key: 'baden-wuerttemberg-bayern',
    label: 'Baden-Württemberg / Bayern',
    prozentsatz: 8
  },
  {
    key: 'andere-bundeslaender',
    label: 'Alle anderen Bundesländer',
    prozentsatz: 9
  }
];

// Sparerfreibeträge (Sparer-Pauschbetrag)
export const SPARERFREIBETRAEGE: SparerfreibetragKonfiguration = {
  alleinstehend: 1000,
  verheiratet: 2000
};

// Tax Rates Constants (Centralized for consistency)
export const TAX_RATES = {
  // --- Abgeltung / Kapitalertragsteuer (calculateEffectiveCapitalGainsTax) ---
  // Capital Gains Tax (Kapitalertragsteuer) - base rate
  KAPITALERTRAGSTEUER_BASE: 0.25,      // 25% base rate

  // Solidaritätszuschlag auf die Kapitalertragsteuer: im Modell pauschal 5,5 % der KapESt
  // (keine Freigrenze/Gleitzone wie bei der Einkommensteuer)
  SOLIDARITAETSZUSCHLAG: 0.055,        // 5.5% on Kapitalertragsteuer amount

  // --- Nur Einkommensteuer (§32a / SolZG ESt-Pfad, z. B. GuenstigerpruefungTab) ---
  // Nicht für calculateEffectiveCapitalGainsTax verwenden
  SOLIDARITAETSZUSCHLAG_GLEITZONE: 0.119, // 11.9% in SolZ-Gleitzone der Einkommensteuer

  // Church Tax (Kirchensteuer) - percentages of income/capital gains tax
  KIRCHENSTEUER_BW_BAYERN: 0.08,       // 8% for Baden-Württemberg/Bayern
  KIRCHENSTEUER_ANDERE: 0.09,          // 9% for other states
  
  // Legacy constant for backwards compatibility (DEPRECATED - use calculateEffectiveCapitalGainsTax)
  KAPITALERTRAGSTEUER: 0.25,           // 25% - use only when no church tax applies
  
  // Combined rates with correct church tax deduction effect
  BASE_CAPITAL_GAINS_RATE: 0.26375,    // 25% + (25% × 5.5%) - no church tax
  // Correct combined rates with church tax deduction effect:
  COMBINED_RATE_BW_BAYERN: 0.2782,     // ~27.82% (effective rate with 8% church tax)
  COMBINED_RATE_ANDERE: 0.2799         // ~27.99% (effective rate with 9% church tax)
} as const;

// Solidaritätszuschlag thresholds 2025 (Freigrenze bezogen auf festgesetzte ESt, §3 Abs. 3 SolZG)
export const SOLI_THRESHOLDS_2025 = {
  FREIGRENZE_SINGLE: 19950,     // €19,950 for single persons
  FREIGRENZE_MARRIED: 39900,    // €39,900 for married couples
  GLEITZONE_MULTIPLIER: 1.2     // 20% above Freigrenze for transition zone
} as const;

// Solidaritätszuschlag thresholds 2026 (Freigrenze bezogen auf festgesetzte ESt, §3 Abs. 3 SolZG)
export const SOLI_THRESHOLDS_2026 = {
  FREIGRENZE_SINGLE: 20350,     // €20,350 for single persons (Einzelveranlagung)
  FREIGRENZE_MARRIED: 40700,    // €40,700 for married couples
  GLEITZONE_MULTIPLIER: 1.2     // 20% above Freigrenze for transition zone
} as const;

// §32a EStG 2026 – Tarifzonen (gesetze-im-internet.de)
export const ESTG_2026 = {
  GRUNDFREIBETRAG: 12348,
  ZONE2_OBERGRENZE: 17799,      // y = (zvE - 12348) / 10000
  ZONE3_OBERGRENZE: 69878,      // z = (zvE - 17799) / 10000
  ZONE4_OBERGRENZE: 277825,
  ZONE2_KOEFF: 914.51,          // (914,51 · y + 1.400) · y
  ZONE3_KOEFF: 173.10,          // (173,10 · z + 2.397) · z + 1.034,87
  ZONE3_KONST: 1034.87,
  ZONE4_KONST: 11135.63,        // 0,42 · x − 11.135,63
  ZONE5_KONST: 19470.38         // 0,45 · x − 19.470,38
} as const;

// §32a EStG 2025 – Tarifzonen (Legacy)
export const ESTG_2025 = {
  GRUNDFREIBETRAG: 12096,
  ZONE2_OBERGRENZE: 17443,
  ZONE3_OBERGRENZE: 68480,
  ZONE4_OBERGRENZE: 277825,
  ZONE2_KOEFF: 932.3,
  ZONE3_KOEFF: 176.64,
  ZONE3_KONST: 1015.13,
  ZONE4_KONST: 10911.92,
  ZONE5_KONST: 19246.67
} as const;

/**
 * Calculates the effective capital gains tax components with correct church tax deduction effect
 * Based on official table "Abgeltungssteuer mit Soli und Kirchensteuer":
 * - 8% (Ba-Wü, Bayern): 24,51% geminderte Abgeltungssteuer + 1,96% anteilige Kirchensteuer + 1,35% anteiliger Soli = 27,82%
 * - 9% (andere Länder): 24,45% geminderte Abgeltungssteuer + 2,20% anteilige Kirchensteuer + 1,34% anteiliger Soli = 27,99%
 * - keine Kirchensteuer: 25% geminderte Abgeltungssteuer + 0% anteilige Kirchensteuer + 1,38% anteiliger Soli = 26,38%
 */
export function calculateEffectiveCapitalGainsTax(
  gewinn: number, 
  kirchensteuerSatz?: string
): {
  kapitalertragsteuer: number;
  solidaritaetszuschlag: number;
  kirchensteuer: number;
  gesamtsteuer: number;
  effectiveRate: number;
} {
  if (gewinn <= 0) {
    return {
      kapitalertragsteuer: 0,
      solidaritaetszuschlag: 0,
      kirchensteuer: 0,
      gesamtsteuer: 0,
      effectiveRate: 0
    };
  }

  let kirchensteuerRate = 0;
  let effectiveKapitalertragsteuerRate = 0.25; // Default 25% for no church tax (exact from screenshot)
  
  if (kirchensteuerSatz === 'baden-wuerttemberg-bayern') {
    kirchensteuerRate = TAX_RATES.KIRCHENSTEUER_BW_BAYERN;
    effectiveKapitalertragsteuerRate = 0.2451; // Exact 24.51% from screenshot
  } else if (kirchensteuerSatz === 'andere-bundeslaender') {
    kirchensteuerRate = TAX_RATES.KIRCHENSTEUER_ANDERE;
    effectiveKapitalertragsteuerRate = 0.2445; // Exact 24.45% from screenshot
  }

  // Rate is now set above based on kirchensteuerSatz
  
  // Calculate individual components
  const kapitalertragsteuer = gewinn * effectiveKapitalertragsteuerRate;
  const solidaritaetszuschlag = kapitalertragsteuer * TAX_RATES.SOLIDARITAETSZUSCHLAG;
  const kirchensteuer = kapitalertragsteuer * kirchensteuerRate;
  const gesamtsteuer = kapitalertragsteuer + solidaritaetszuschlag + kirchensteuer;
  
  return {
    kapitalertragsteuer: Math.round(kapitalertragsteuer * 10000) / 10000,
    solidaritaetszuschlag: Math.round(solidaritaetszuschlag * 10000) / 10000,
    kirchensteuer: Math.round(kirchensteuer * 10000) / 10000,
    gesamtsteuer: Math.round(gesamtsteuer * 10000) / 10000,
    effectiveRate: gesamtsteuer / gewinn
  };
}

// Navigation-Tabs
export const NAVIGATION_TABS = [
  {
    id: 'schritt0',
    label: 'Schritt 0: Info und FAQ',
    pfad: '/schritt0'
  },
  {
    id: 'schritt1',
    label: 'Schritt 1: Berechnungsparameter',
    pfad: '/schritt1'
  },
  {
    id: 'schritt2',
    label: 'Schritt 2: Transaktionsdaten importieren',
    pfad: '/schritt2'
  },
  {
    id: 'schritt3',
    label: 'Schritt 3: Beispielhafte Simulation von Verkaufs-Szenarien',
    pfad: '/schritt3'
  },
  {
    id: 'schritt4',
    label: 'Schritt 4: Modellvergleich: Einkommensteuer vs. Abgeltungsteuer (Beispielrechnung)',
    pfad: '/schritt4'
  }
];